import React, { useState, useMemo } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import EditableFieldsTable from "../configuration/EditableFieldsTable";
import SearchField from "../configuration/SearchField";
import getBillingLabel from "../../../utils/billing/getBillingLabel";
import useQueryParams from "../../../hooks/useQueryParams";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flexGrow: 1,
  },
  tableContainer: {
    width: "100%",
  },
  table: {
    fontSize: 16,
  },
};

export default function MiscRates({ entityPath = "", dropdownComponent, readOnly = false, focusSection = "" }) {
  const { values } = useFormikContext();
  const queryParams = useQueryParams();
  const defaultSearch = queryParams.get("search") || "";
  const [search, setSearch] = useState(defaultSearch);

  const joinPaths = (...parts) => parts.filter(Boolean).join(".");

  const filterObject = (obj, term) => {
    if (!obj) return {};
    if (!term.trim()) return obj;
    const terms = term
      .toLowerCase()
      .split(",")
      .map(t => t.trim())
      .filter(Boolean);

    return Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => {
        const label = (getBillingLabel(key, value) || "").toLowerCase();
        return terms.some(t => label.includes(t));
      })
    );
  };

  const filteredLaborRates = useMemo(
    () => filterObject(get(values, joinPaths(entityPath, "laborRates")), search),
    [get(values, joinPaths(entityPath, "laborRates")), search]
  );
  const filteredMaterialRates = useMemo(
    () => filterObject(get(values, joinPaths(entityPath, "materialRates")), search),
    [get(values, joinPaths(entityPath, "materialRates")), search]
  );
  const filteredOpenInspectRates = useMemo(
    () => filterObject(get(values, joinPaths(entityPath, "openInspectRates")), search),
    [get(values, joinPaths(entityPath, "openInspectRates")), search]
  );
  const filteredEquipmentRates = useMemo(
    () => filterObject(get(values, joinPaths(entityPath, "equipmentRates")), search),
    [get(values, joinPaths(entityPath, "equipmentRates")), search]
  );

  const focusRateKey = focusSection ? `${focusSection}Rates` : "";

  const allSections = [
    {
      key: "laborRates",
      title: "Labor Rates",
      base: joinPaths(entityPath, "laborRates"),
      data: filteredLaborRates,
      unit: "per hour",
    },
    {
      key: "materialRates",
      title: "Material Rates",
      base: joinPaths(entityPath, "materialRates"),
      data: filteredMaterialRates,
      unit: "each",
    },
    {
      key: "openInspectRates",
      title: "Open & Inspect Rates",
      base: joinPaths(entityPath, "openInspectRates"),
      data: filteredOpenInspectRates,
      unit: "per item",
    },
    {
      key: "equipmentRates",
      title: "Equipment Rates",
      base: joinPaths(entityPath, "equipmentRates"),
      data: filteredEquipmentRates,
      unit: "each",
    },
  ];

  const sections = focusRateKey
    ? allSections.filter(s => s.key === focusRateKey)
    : allSections;

  return (
    <Box sx={styles.container}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        {dropdownComponent}
        <SearchField value={search} onChange={setSearch} placeholder="Search" />
      </Box>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table size="small" stickyHeader sx={styles.table}>
          {sections.map(
            ({ title, base, data, unit }) =>
              Object.keys(data || {}).length > 0 && (
                <EditableFieldsTable
                  key={base}
                  title={title}
                  baseName={base}
                  fieldsObject={data}
                  unit={unit}
                  readOnly={readOnly}
                />
              )
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}
